import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { Sparkles, RefreshCw, ThumbsUp, ThumbsDown, Clock, Eye, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function VisionStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.visionStats().then(setStats).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  if (!stats) return <div className="text-slate-500 font-mono text-xs">Loading vision metrics…</div>;

  const o = stats.overall;
  // Display rate with 1 decimal when below 90% so e.g. 89.5% never rounds up into the "High" branch visually.
  const ratePct = (r) => {
    if (r == null) return "—";
    if (r >= 0.9) return `${Math.round(r * 100)}%`;
    return `${(Math.floor(r * 1000) / 10).toFixed(1)}%`;
  };
  const confPct = (c) => c == null ? "—" : `${Math.round(c * 100)}%`;
  const rateClass = (r) => r == null ? "sev-info" : r >= 0.9 ? "sev-ok" : r >= 0.6 ? "sev-warning" : "sev-critical";

  const chartData = stats.terminals.map((t) => ({
    name: (t.terminal_name || "—").slice(0, 18),
    accepted: t.accepted,
    rejected: t.rejected,
    pending: t.pending,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Sparkles size={11} className="text-sky-400" /> AI Quality
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Vision Detection Metrics</h1>
          <div className="font-mono text-[11px] text-slate-500 mt-1">
            Per-terminal accept rate, confidence, and triage status for Claude Sonnet 4.5 map auto-detection.
          </div>
          <Link to="/help#vision-metrics" className="text-xs text-sky-300 hover:text-sky-200">How to use these metrics</Link>
        </div>
        <Button data-testid="btn-refresh-vision-stats" size="sm" onClick={refresh} disabled={loading} className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200">
          <RefreshCw size={14} className={loading ? "mr-1.5 animate-spin" : "mr-1.5"} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="vision-overall-kpis">
        <Kpi label="Total detections" value={o.total} sub="ever auto-detected" />
        <Kpi label="Accepted" value={o.accepted} sub="kept by operator" className="text-emerald-400" />
        <Kpi label="Rejected" value={o.rejected} sub="discarded" className="text-red-400" />
        <Kpi label="Pending review" value={o.pending} sub="awaiting triage" className="text-amber-400" />
        <Kpi label="Overall accept rate" value={ratePct(o.accept_rate)} sub={`avg conf ${confPct(o.avg_confidence)}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">Triage Status by Terminal</div>
          {chartData.length === 0 ? (
            <div className="text-xs font-mono text-slate-500">No detections yet. Try uploading a terminal map and clicking "AI detect".</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} stackOffset="none">
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#64748B" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <Tooltip contentStyle={{ background: "#0E1522", border: "1px solid #1E293B", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <Bar dataKey="accepted" stackId="s" fill="#10B981" />
                <Bar dataKey="rejected" stackId="s" fill="#EF4444" />
                <Bar dataKey="pending" stackId="s" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="flex gap-4 mt-2 text-[10px] font-mono">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 bg-emerald-500"></span>Accepted</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 bg-red-500"></span>Rejected</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 bg-amber-500"></span>Pending</span>
          </div>
        </div>

        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3 flex items-center gap-1.5"><TrendingUp size={11} /> Trust Recommendation</div>
          {o.accept_rate == null ? (
            <div className="text-xs text-slate-500">Not enough decisions yet to recommend.</div>
          ) : o.accept_rate >= 0.9 ? (
            <div className="text-sm text-emerald-400">
              <ThumbsUp size={14} className="inline mr-1" />
              High operator confidence ({ratePct(o.accept_rate)}). You may consider auto-accepting future detections on high-rate terminals.
            </div>
          ) : o.accept_rate >= 0.6 ? (
            <div className="text-sm text-amber-300">
              <Eye size={14} className="inline mr-1" />
              Moderate accept rate ({ratePct(o.accept_rate)}). Continue manual triage — the model is useful but inconsistent.
            </div>
          ) : (
            <div className="text-sm text-red-300">
              <ThumbsDown size={14} className="inline mr-1" />
              Low accept rate ({ratePct(o.accept_rate)}). Investigate prompt calibration or upload higher-resolution maps before trusting bulk-accept.
            </div>
          )}
        </div>
      </div>

      <div className="panel p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">Per-terminal Detail</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              <th className="text-left pb-2">Terminal</th>
              <th className="text-left pb-2">Total</th>
              <th className="text-left pb-2">Accepted</th>
              <th className="text-left pb-2">Rejected</th>
              <th className="text-left pb-2">Pending</th>
              <th className="text-left pb-2">Accept rate</th>
              <th className="text-left pb-2">Avg confidence</th>
              <th className="text-left pb-2">Last detection</th>
            </tr>
          </thead>
          <tbody>
            {stats.terminals.length === 0 && <tr><td colSpan={8} className="text-slate-500 py-3">No detections yet.</td></tr>}
            {stats.terminals.map(t => (
              <tr key={t.terminal_id} className="border-b border-[#1E293B] hover:bg-[#0E1522]" data-testid={`stats-row-${t.terminal_id}`}>
                <td className="py-2 text-slate-100">{t.terminal_name}</td>
                <td className="font-mono py-2">{t.total}</td>
                <td className="font-mono py-2 text-emerald-300">{t.accepted}</td>
                <td className="font-mono py-2 text-red-300">{t.rejected}</td>
                <td className="font-mono py-2 text-amber-300">{t.pending}</td>
                <td><span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${rateClass(t.accept_rate)}`}>{ratePct(t.accept_rate)}</span></td>
                <td className="font-mono py-2 text-slate-400">{confPct(t.avg_confidence)}</td>
                <td className="font-mono py-2 text-[10px] text-slate-500 flex items-center gap-1"><Clock size={10} />{t.last_detected ? t.last_detected.slice(0, 16).replace("T", " ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, className = "" }) {
  return (
    <div className="panel p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">{label}</div>
      <div className={`font-heading text-2xl font-black tracking-tighter ${className || "text-slate-50"}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
